import { afterNextRender, Directive, ElementRef, inject, input, numberAttribute, OnInit } from '@angular/core'
import { NgControl } from '@angular/forms'
import { fromEvent, merge, takeUntil } from 'rxjs'

import { LoggerService } from 'app/core/logger/logger.service'
import { ResizeService } from 'app/core/resize.service'
import { onDestroy } from 'app/utils/rxjs/on-destroy.observable'

/**
 * Standalone AutosizeDirective for TextArea
 * ONLY works with Reactive Forms (necessary to use FormControlDirective, therefore the selector)
 * there would be a cleaner approach for the autosized textarea, but harder to implement as directive:
 * https://css-tricks.com/the-cleanest-trick-for-autogrowing-textareas/
 */
@Directive({
  selector: 'textarea[formControl][paiAutosize]',
  standalone: true,
  host: {
    '[rows]': 'rows()',
    '[style.resize]': '"none"',
    '[style.overflow]': '"hidden"',
  },
})
export class TextareaAutosizeDirective implements OnInit {
  readonly rows = input(1, { transform: numberAttribute })

  readonly element: HTMLTextAreaElement = inject(ElementRef).nativeElement

  private readonly logger = inject(LoggerService).getInstance('TextareaAutosizeDirective')
  private readonly onDestroy$ = onDestroy()
  private readonly ngControl = inject(NgControl)

  constructor() {
    const resizeService = inject(ResizeService)

    afterNextRender(this.resize)

    merge([resizeService.observe(this.element), fromEvent(this.element, 'input')])
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(this.resize)
  }

  ngOnInit() {
    this.ngControl.valueChanges?.pipe(takeUntil(this.onDestroy$)).subscribe(this.resize.bind(this))
  }

  private readonly resize = () => {
    this.logger.debug('Resizing textarea')
    // Calculate border height which is not included in scrollHeight
    const borderHeight = this.element.offsetHeight - this.element.clientHeight

    // Reset textarea height to auto that correctly calculate the new height
    this.element.style.height = 'auto'

    const newVal = this.element.scrollHeight + borderHeight
    // Set new height
    this.element.style.height = isNaN(newVal) ? 'auto' : `${newVal}px`
  }
}
